<template>
    <div class="admin-content-container">
        <PageDescriptionEditor page-title="Books"
            default-description="Explore our catalog of books — from new releases to coming-soon titles." />
        <div class="space-y-8">
            <div>
                <h2 class="font-header text-[3rem] font-bold text-primary">Books Management</h2>
            </div>

            <!-- Category Management Section -->
            <div class="space-y-6">
                <div class="flex justify-between items-center">
                    <h3 class="font-header text-[1.5rem] font-bold text-primary">Category Management</h3>
                    <button @click="showCategoryForm = !showCategoryForm"
                        class="px-6 py-3 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold">
                        {{ showCategoryForm ? 'Cancel' : 'Add Category' }}
                    </button>
                </div>

                <!-- Add/Edit Category Form -->
                <div v-if="showCategoryForm" class="border border-gray-200 rounded-lg p-8 md:p-12">
                    <form @submit.prevent="saveCategory" class="space-y-4">
                        <div>
                            <label class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">Category
                                Name *</label>
                            <input v-model="categoryForm.name" type="text" required
                                class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                        </div>
                        <div>
                            <label class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">Description
                                (Optional)</label>
                            <textarea v-model="categoryForm.description" rows="2"
                                class="w-full px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"></textarea>
                        </div>
                        <div class="flex gap-3 justify-end pt-4 border-t border-gray-200">
                            <button type="submit"
                                class="px-8 py-3 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-body text-[1.125rem] leading-[1.75]">
                                {{ editingCategory ? 'Update Category' : 'Create Category' }}
                            </button>
                            <button type="button" @click="cancelCategoryForm"
                                class="px-8 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-body text-[1.125rem] leading-[1.75]">
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>

                <!-- Categories Table -->
                <div class="border border-gray-200 rounded-lg overflow-hidden">
                    <div class="p-6 border-b border-gray-200">
                        <h3 class="font-header text-[1.5rem] font-bold text-primary">All Categories</h3>
                    </div>
                    <div class="overflow-x-auto">
                        <table class="w-full">
                            <thead class="bg-accent text-white">
                                <tr>
                                    <th class="px-6 py-4 text-left font-body text-[1.125rem] leading-[1.75]">
                                        Name
                                    </th>
                                    <th
                                        class="hidden sm:table-cell px-6 py-4 text-left font-body text-[1.125rem] leading-[1.75]">
                                        Description
                                    </th>
                                    <th class="px-6 py-4 text-left font-body text-[1.125rem] leading-[1.75]">
                                        Type
                                    </th>
                                    <th class="px-6 py-4 text-left font-body text-[1.125rem] leading-[1.75]">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="category in sortedCategories" :key="category.id"
                                    class="border-b border-gray-200 hover:bg-creme-50 transition-colors">
                                    <td class="px-6 py-4">
                                        <div class="font-body text-[1.125rem] leading-[1.75] text-gray-900">{{
                                            category.name }}</div>
                                        <div v-if="category.description"
                                            class="sm:hidden text-xs text-gray-600 font-body leading-[1.75] mt-1">
                                            {{ category.description }}
                                        </div>
                                    </td>
                                    <td
                                        class="hidden sm:table-cell px-6 py-4 font-body text-[1.125rem] leading-[1.75] text-gray-700">
                                        {{ category.description || '-' }}
                                    </td>
                                    <td class="px-6 py-4">
                                        <span v-if="category.is_custom"
                                            class="inline-block px-2 py-1 bg-accent text-white text-xs rounded font-body leading-[1.75]">
                                            Custom
                                        </span>
                                        <span v-else
                                            class="inline-block px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded font-body leading-[1.75]">
                                            Default
                                        </span>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex gap-2">
                                            <button @click="editCategory(category)" type="button"
                                                class="text-green-700 hover:text-green-800 font-body text-[1.125rem] leading-[1.75]">
                                                Edit
                                            </button>
                                            <button @click="deleteCategoryById(category.id)" type="button"
                                                class="text-red-600 hover:text-red-700 font-marketing">
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="showForm" class="border border-gray-200 rounded-lg p-8 md:p-12">
                <div class="flex justify-between items-center mb-8">
                    <h3 class="text-2xl md:text-3xl font-marketing font-bold text-primary">{{ editingBook ? 'Edit' :
                        'Add' }} Book</h3>
                    <button @click="cancelForm" class="text-gray-600 hover:text-gray-800 font-marketing">
                        Cancel
                    </button>
                </div>
                <form @submit.prevent="saveBook" class="space-y-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Title *</label>
                            <input v-model="formData.title" type="text" required
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                        </div>
                        <div>
                            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Author *</label>
                            <div class="space-y-3">
                                <select v-model="formData.selectedAuthorId" @change="onAuthorSelect"
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
                                    <option value="">Select from existing authors</option>
                                    <option v-for="author in authors" :key="author.id" :value="author.id">
                                        {{ author.name }}
                                    </option>
                                </select>
                                <input v-model="formData.author" type="text" required placeholder="Or enter author name"
                                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                            </div>
                        </div>
                        <div class="md:col-span-2">
                            <label class="block text-sm font-marketing font-semibold text-primary mb-3">Formats
                                *</label>
                            <div class="space-y-4">
                                <div v-for="(format, index) in formData.formats" :key="index"
                                    class="p-4 border border-gray-300 rounded-md bg-white">
                                    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                                        <div>
                                            <label
                                                class="block text-xs font-marketing font-semibold text-primary mb-1">Format
                                                Type *</label>
                                            <select v-model="format.format_type" required
                                                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm">
                                                <option value="">Select format</option>
                                                <option value="hardcover">Hardcover</option>
                                                <option value="paperback">Paperback</option>
                                                <option value="ebook">E-book</option>
                                                <option value="audiobook">Audiobook</option>
                                                <option value="custom">Custom</option>
                                            </select>
                                            <input v-if="format.format_type === 'custom'"
                                                v-model="format.custom_format_type" type="text"
                                                placeholder="Enter custom format"
                                                class="w-full mt-2 px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                        </div>
                                        <div>
                                            <label
                                                class="block text-xs font-marketing font-semibold text-primary mb-1">ISBN
                                                *</label>
                                            <input v-model="format.isbn" type="text" required
                                                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                        </div>
                                        <div>
                                            <label
                                                class="block text-xs font-marketing font-semibold text-primary mb-1">Price
                                                *</label>
                                            <input v-model.number="format.price" type="number" step="0.01" min="0"
                                                required
                                                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                        </div>
                                        <div>
                                            <label
                                                class="block text-xs font-marketing font-semibold text-primary mb-1">Discount
                                                %</label>
                                            <input v-model.number="format.discount_percentage" type="number" step="0.1"
                                                min="0" max="100"
                                                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                        </div>
                                    </div>
                                    <div class="mt-3">
                                        <label class="flex items-center gap-2">
                                            <input v-model="format.is_unavailable" type="checkbox"
                                                class="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent" />
                                            <span class="text-sm font-marketing font-semibold text-primary">Mark as
                                                unavailable</span>
                                        </label>
                                    </div>
                                    <div v-if="isDigitalFormat(format)" class="mt-4 p-3 rounded-md bg-creme-50 border border-creme-300">
                                        <p class="text-xs font-marketing font-semibold text-primary mb-2">Digital
                                            file</p>
                                        <p class="text-xs text-gray-600 font-sans mb-3">Upload the file customers
                                            receive after purchase. Any file type or zip is allowed.</p>
                                        <div class="flex flex-wrap items-center gap-3 mb-3">
                                            <input :ref="el => setDigitalFileInput(index, el)" type="file"
                                                @change="(e) => handleDigitalFileSelect(index, e, false)"
                                                class="hidden" />
                                            <button type="button"
                                                @click="digitalFileInputs[index]?.click()"
                                                :disabled="format.digital_uploading"
                                                class="px-4 py-2 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold text-sm disabled:opacity-50">
                                                {{ format.digital_uploading ? 'Uploading…' : (format.digital_file_id ? 'Replace file' : 'Upload file') }}
                                            </button>
                                            <span v-if="format.digital_file_name" class="text-xs text-gray-700 font-sans">
                                                {{ format.digital_file_name }}
                                            </span>
                                            <button v-if="format.digital_file_id" type="button"
                                                @click="clearDigitalFile(index, false)"
                                                class="text-xs text-red-600 hover:text-red-700 font-marketing">
                                                Remove
                                            </button>
                                        </div>
                                        <div>
                                            <label class="flex items-center gap-2 mb-2">
                                                <input v-model="format.is_sample_available" type="checkbox"
                                                    class="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent" />
                                                <span class="text-xs font-marketing font-semibold text-primary">
                                                    Offer a free sample
                                                </span>
                                            </label>
                                            <div v-if="format.is_sample_available" class="flex flex-wrap items-center gap-3">
                                                <input :ref="el => setSampleFileInput(index, el)" type="file"
                                                    @change="(e) => handleDigitalFileSelect(index, e, true)"
                                                    class="hidden" />
                                                <button type="button"
                                                    @click="sampleFileInputs[index]?.click()"
                                                    :disabled="format.sample_uploading"
                                                    class="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold text-sm disabled:opacity-50">
                                                    {{ format.sample_uploading ? 'Uploading…' : (format.sample_file_id ? 'Replace sample' : 'Upload sample') }}
                                                </button>
                                                <span v-if="format.sample_file_name" class="text-xs text-gray-700 font-sans">
                                                    {{ format.sample_file_name }}
                                                </span>
                                                <button v-if="format.sample_file_id" type="button"
                                                    @click="clearDigitalFile(index, true)"
                                                    class="text-xs text-red-600 hover:text-red-700 font-marketing">
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mb-4">
                                        <label class="block text-xs font-marketing font-semibold text-primary mb-2">Also
                                            Available From</label>
                                        <div class="space-y-3">
                                            <div v-for="(retailer, retailerIndex) in format.retailers"
                                                :key="retailerIndex" class="flex gap-2 items-start">
                                                <div class="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    <input v-model="retailer.name" type="text"
                                                        placeholder="Merchant Name"
                                                        class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                                    <input v-model="retailer.link" type="url"
                                                        placeholder="Merchant Link (https://...)"
                                                        class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm" />
                                                </div>
                                                <button type="button" @click="removeRetailer(index, retailerIndex)"
                                                    class="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors font-marketing font-semibold">
                                                    ×
                                                </button>
                                            </div>
                                            <button type="button" @click="addRetailer(index)"
                                                class="w-full px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-marketing font-semibold">
                                                + Add Retailer
                                            </button>
                                        </div>
                                    </div>
                                    <div class="flex justify-end">
                                        <button type="button" @click="removeFormat(index)"
                                            class="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors font-marketing font-semibold">
                                            Remove Format
                                        </button>
                                    </div>
                                </div>
                                <button type="button" @click="addFormat"
                                    class="w-full px-4 py-2 bg-accent text-white rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold">
                                    + Add Format
                                </button>
                            </div>
                        </div>
                        <div>
                            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Published
                                Date</label>
                            <input v-model="formData.published_date" type="date"
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                        </div>
                        <div>
                            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Page
                                Count</label>
                            <input v-model.number="formData.page_count" type="number"
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                        </div>
                        <div>
                            <label
                                class="flex items-center gap-3 p-3 rounded-md border border-gray-300 hover:bg-creme-50 cursor-pointer transition-colors">
                                <input type="checkbox" v-model="formData.is_unavailable"
                                    class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2" />
                                <span class="text-sm font-marketing font-semibold text-primary">Mark as
                                    Unavailable</span>
                            </label>
                            <p class="text-xs text-gray-600 font-sans mt-2">
                                <strong>Unavailable</strong> = not for sale yet. The book shows only in the
                                “Coming Soon” section and can’t be added to cart or bought by anyone. Use it for
                                upcoming or out-of-stock titles.
                            </p>
                        </div>
                        <div>
                            <label
                                class="flex items-center gap-3 p-3 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 cursor-pointer transition-colors">
                                <input type="checkbox" v-model="formData.is_test"
                                    class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2" />
                                <span class="text-sm font-marketing font-semibold text-primary">Mark as Test
                                    (admin-only)</span>
                            </label>
                            <p class="text-xs text-gray-600 font-sans mt-2">
                                <strong>Test</strong> = visible and purchasable by <strong>admin accounts only</strong>.
                                Normal customers never see it in the catalog or search and can’t buy it (incl. its
                                e-book/audiobook). Use it to safely verify the reader, player and checkout — even in
                                production. Untick to turn it into a normal book available to everyone.
                            </p>
                        </div>
                        <div class="md:col-span-2">
                            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Cover
                                Image</label>
                            <div class="space-y-3">
                                <div class="flex gap-3">
                                    <input ref="fileInput" type="file" accept="image/*,.heic,.heif"
                                        @change="handleFileSelect" class="hidden" />
                                    <button type="button" @click="() => fileInput?.click()"
                                        class="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold">
                                        Upload Image
                                    </button>
                                    <input v-model="formData.cover_image" type="url" placeholder="Or enter image URL"
                                        class="flex-1 px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
                                </div>
                                <div v-if="imagePreview" class="mt-3">
                                    <div class="flex gap-3 items-start">
                                        <img :src="imagePreview" alt="Preview" :style="getImageStyle()"
                                            class="rounded-md border border-creme-300" />
                                        <div class="flex flex-col gap-2">
                                            <button type="button" @click="loadImageForCropping"
                                                :disabled="showCropModal || loadingCropImage"
                                                :class="['px-4 py-2 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold text-sm', (showCropModal || loadingCropImage) ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : '']">
                                                {{ loadingCropImage ? 'Loading...' : 'Crop Image' }}
                                            </button>
                                            <button type="button" @click="clearImage"
                                                class="px-4 py-2 text-sm text-red-600 hover:text-red-700 font-marketing">
                                                Remove Image
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="formData.image_crop_dimensions"
                                        class="mt-2 text-xs text-gray-600 font-sans">
                                        Dimensions: {{ Math.round(formData.image_crop_dimensions.width) }} × {{
                                            Math.round(formData.image_crop_dimensions.height) }}
                                    </div>
                                </div>
                                <div v-else-if="formData.cover_image && !uploading" class="mt-3">
                                    <div class="flex gap-3 items-start">
                                        <img :src="formData.cover_image" alt="Preview" :style="getImageStyle()"
                                            class="rounded-md border border-creme-300" />
                                        <button type="button" @click="() => loadImageForCropping()"
                                            class="px-4 py-2 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold text-sm">
                                            Crop Image
                                        </button>
                                    </div>
                                    <div v-if="formData.image_crop_dimensions"
                                        class="mt-2 text-xs text-gray-600 font-sans">
                                        Dimensions: {{ Math.round(formData.image_crop_dimensions.width) }} × {{
                                            Math.round(formData.image_crop_dimensions.height) }}
                                    </div>
                                </div>
                                <div v-if="uploading" class="text-sm text-gray-600 font-sans">
                                    Uploading...
                                </div>
                            </div>
                        </div>
                        <div class="md:col-span-2">
                            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Categories
                                *</label>
                            <p class="text-xs text-gray-600 font-sans mb-3">Select from existing categories. To add new
                                categories, use the Category Management section above.</p>
                            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                                <label v-for="genreOption in genreOptions" :key="genreOption.value"
                                    class="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-md border hover:bg-creme-50 cursor-pointer transition-colors"
                                    :class="genreOption.is_custom ? 'border-accent bg-creme-50' : 'border-gray-300'">
                                    <input type="checkbox" :value="genreOption.value" v-model="formData.genre"
                                        class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded flex-shrink-0" />
                                    <span class="text-sm font-sans text-gray-900 flex-1 min-w-0">{{ genreOption.label
                                        }}</span>
                                    <span v-if="genreOption.is_custom"
                                        class="text-xs text-accent font-sans flex-shrink-0">Custom</span>
                                </label>
                            </div>
                        </div>
                        <div class="md:col-span-2">
                            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Description
                                *</label>
                            <textarea v-model="formData.description" rows="6" required
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans resize-none"></textarea>
                        </div>
                    </div>
                    <div class="flex gap-3 pt-4 border-t border-gray-200">
                        <button type="submit"
                            class="px-8 py-3 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold">
                            {{ editingBook ? 'Update Book' : 'Create Book' }}
                        </button>
                        <button type="button" @click="cancelForm"
                            class="px-8 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>

            <div v-if="!showForm" class="space-y-6">
                <div class="flex justify-end gap-3">
                    <button @click="toggleShowDeleted"
                        class="px-6 py-3 rounded-md border font-marketing font-semibold transition-colors"
                        :class="showDeleted ? 'border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'">
                        {{ showDeleted ? 'Hide deleted books' : 'Show deleted books' }}
                    </button>
                    <button @click="addNewBook"
                        class="px-6 py-3 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold">
                        Add Book
                    </button>
                </div>

                <div class="border border-gray-200 rounded-lg overflow-hidden">
                    <div class="p-6 border-b border-gray-200">
                        <h3 class="text-xl font-marketing font-bold text-primary">All Books</h3>
                    </div>
                    <div class="overflow-x-auto">
                        <table class="w-full">
                            <thead class="bg-accent text-white">
                                <tr>
                                    <th class="px-6 py-4 text-left font-marketing font-semibold">Title</th>
                                    <th class="px-6 py-4 text-left font-marketing font-semibold">Author</th>
                                    <th class="px-6 py-4 text-left font-marketing font-semibold">Price</th>
                                    <th class="px-6 py-4 text-left font-marketing font-semibold">Format</th>
                                    <th class="px-6 py-4 text-left font-marketing font-semibold">Discount</th>
                                    <th class="px-6 py-4 text-left font-marketing font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="books.length === 0">
                                    <td colspan="6" class="px-6 py-12 text-center text-gray-500 font-sans">
                                        No books found. Click "Add Book" to create one.
                                    </td>
                                </tr>
                                <tr v-for="book in books" :key="book.id"
                                    class="border-b border-gray-200 hover:bg-creme-50 transition-colors"
                                    :class="{ 'opacity-60 bg-gray-50': book.deleted_at }">
                                    <td class="px-6 py-4 font-marketing text-gray-900 font-medium">
                                        {{ book.title }}
                                        <span v-if="book.deleted_at"
                                            class="ml-2 px-2.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-marketing font-semibold">
                                            Deleted — hidden from store
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 font-sans text-gray-700">{{ book.author }}</td>
                                    <td class="px-6 py-4 font-sans text-gray-700">${{ typeof book.price === 'number' ?
                                        book.price.toFixed(2)
                                        : parseFloat(book.price || 0).toFixed(2) }}</td>
                                    <td class="px-6 py-4 font-sans capitalize text-gray-700">{{ book.format }}</td>
                                    <td class="px-6 py-4 font-sans text-gray-700">{{ book.discount_percentage || 0 }}%
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex gap-4">
                                            <button @click="editBook(book)"
                                                class="text-accent hover:text-green-700 font-marketing font-semibold transition-colors">Edit</button>
                                            <button v-if="book.deleted_at" @click="restoreBook(book.id)"
                                                class="text-accent hover:text-green-700 font-marketing font-semibold transition-colors">Restore</button>
                                            <button v-else @click="deleteBook(book.id)"
                                                class="text-red-600 hover:text-red-700 font-marketing font-semibold transition-colors">Delete</button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-if="pagination && pagination.total_pages > 1"
                        class="p-4 flex justify-between items-center border-t border-creme-200">
                        <button @click="loadBooks(currentPage - 1)" :disabled="!pagination.prev_page"
                            :class="['px-4 py-2 rounded-md font-marketing', pagination.prev_page ? 'bg-accent text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                            Previous
                        </button>
                        <span class="font-marketing text-gray-700">
                            Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
                        </span>
                        <button @click="loadBooks(currentPage + 1)" :disabled="!pagination.next_page"
                            :class="['px-4 py-2 rounded-md font-marketing', pagination.next_page ? 'bg-accent text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <BaseModal v-model="showCropModal" content-class="max-w-4xl max-h-[90vh]" @close="closeCropModal">
            <div class="mb-4">
                <h3 class="text-xl font-marketing font-bold text-primary">Crop Image</h3>
            </div>
            <div v-if="cropImageSrc" class="mb-4 relative" style="height: 700px; width: 100%;">
                <Cropper :key="`cropper-${cropImageSrc}`" ref="cropper" :src="cropImageSrc" :default-boundaries="'fill'"
                    :stencil-props="{
                        aspectRatio: null
                    }" @change="onCropChange" @ready="onCropperReady" style="width: 100%; height: 100%;" />
            </div>
            <div v-else class="mb-4 flex items-center justify-center" style="height: 700px;">
                <p class="text-gray-500 font-sans">No image selected</p>
            </div>

            <div v-if="cropImageSrc && canZoomOut" class="mb-4 flex justify-center">
                <button @click="fitImageToCrop"
                    class="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-marketing font-semibold text-sm shadow-sm flex items-center gap-2"
                    title="Fit image to crop area (zoom out)">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                    </svg>
                    Fit to Crop
                </button>
            </div>

            <div v-if="cropResult && cropResult.coordinates"
                class="mb-6 p-4 bg-creme-100 rounded-lg border border-creme-300">
                <h4 class="text-sm font-marketing font-semibold text-primary mb-3">Preview: How it will appear alongside
                    other books</h4>
                <div class="flex items-end gap-4 justify-center">
                    <div class="flex flex-col items-center">
                        <div :style="getPreviewBookStyle(cropResult.coordinates)"
                            class="bg-white border-2 border-gray-300 shadow-md mb-2 overflow-hidden">
                            <img v-if="previewImageUrl" :src="previewImageUrl" alt="Preview"
                                class="w-full h-full object-cover" />
                        </div>
                        <p class="text-xs text-gray-600 font-sans">Current Book</p>
                        <p class="text-xs text-gray-500 font-sans mt-1">{{ Math.round(cropResult.coordinates.width) }} ×
                            {{ Math.round(cropResult.coordinates.height) }}px</p>
                    </div>
                    <div class="flex flex-col items-center">
                        <div :style="getReferenceBookStyle()"
                            class="bg-white border-2 border-gray-300 shadow-md mb-2 overflow-hidden">
                            <div
                                class="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </div>
                        </div>
                        <p class="text-xs text-gray-600 font-sans">Reference Book</p>
                        <p class="text-xs text-gray-500 font-sans mt-1">168 × 224px</p>
                    </div>
                </div>
                <div v-if="cropResult.coordinates.width > 200"
                    class="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 font-sans">
                    ⚠️ Width exceeds maximum threshold (200px). Book will be scaled down to fit.
                </div>
            </div>

            <div class="flex gap-3 justify-end">
                <button @click="closeCropModal"
                    class="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold">
                    Cancel
                </button>
                <button @click="applyCrop"
                    class="px-6 py-2 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold">
                    Apply Crop
                </button>
            </div>
        </BaseModal>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { adminService } from '../../services/admin'
import { API_BASE_URL } from '../../config'
import BaseModal from './BaseModal.vue'
import PageDescriptionEditor from './PageDescriptionEditor.vue'
import { useScrollToTop } from '../../composables/useScrollToTop'

const { scrollToTop, watchAndScroll } = useScrollToTop()

const books = ref([])
const authors = ref([])
const categories = ref([])
const showForm = ref(false)

// Watch showForm and scroll to top when it becomes true
watchAndScroll(showForm)

const editingBook = ref(null)
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)
const imagePreview = ref('')
const uploading = ref(false)
const fileInput = ref(null)
const digitalFileInputs = ref([])
const sampleFileInputs = ref([])
const originalDigitalProducts = ref([])

const setDigitalFileInput = (index, el) => {
    if (el) digitalFileInputs.value[index] = el
}
const setSampleFileInput = (index, el) => {
    if (el) sampleFileInputs.value[index] = el
}

const isDigitalFormat = (format) => {
    return format && (format.format_type === 'ebook' || format.format_type === 'audiobook')
}

const defaultFormat = () => ({
    format_type: '',
    custom_format_type: '',
    isbn: '',
    price: 0,
    discount_percentage: null,
    is_unavailable: false,
    retailers: [],
    digital_product_id: null,
    digital_file_id: null,
    digital_file_name: '',
    digital_file_format: '',
    sample_file_id: null,
    sample_file_name: '',
    is_sample_available: false,
    digital_uploading: false,
    sample_uploading: false
})

const handleDigitalFileSelect = async (formatIndex, event, isSample) => {
    const file = event.target.files && event.target.files[0]
    if (!file) return
    const format = formData.value.formats[formatIndex]
    if (!format) return

    const flagKey = isSample ? 'sample_uploading' : 'digital_uploading'
    format[flagKey] = true
    try {
        const category = isSample ? 'digital_sample' : 'digital_product'
        const result = await adminService.uploadDigitalFile(file, category)
        if (isSample) {
            format.sample_file_id = result.file_id
            format.sample_file_name = result.original_filename || file.name
        } else {
            format.digital_file_id = result.file_id
            format.digital_file_name = result.original_filename || file.name
            format.digital_file_format = result.file_format || ''
        }
    } catch (error) {
        console.error('Failed to upload digital file:', error)
        alert('Failed to upload file. Please try again.')
    } finally {
        format[flagKey] = false
        const inputEl = isSample ? sampleFileInputs.value[formatIndex] : digitalFileInputs.value[formatIndex]
        if (inputEl) inputEl.value = ''
    }
}

const clearDigitalFile = (formatIndex, isSample) => {
    const format = formData.value.formats[formatIndex]
    if (!format) return
    if (isSample) {
        format.sample_file_id = null
        format.sample_file_name = ''
    } else {
        format.digital_file_id = null
        format.digital_file_name = ''
        format.digital_file_format = ''
    }
}

// Reconcile digital products for a book against the form's formats.
// - Format with format_type ebook|audiobook AND digital_file_id set -> ensure
//   a digital_products row exists (POST new, PUT existing).
// - Existing digital_products that no longer have a matching format with a
//   file get DELETEd.
const syncDigitalProducts = async (bookId) => {
    const desired = formData.value.formats
        .filter(f => isDigitalFormat(f) && f.digital_file_id)
        .map(f => ({
            digital_product_id: f.digital_product_id || null,
            book_id: bookId,
            format: f.format_type,
            file_format: f.digital_file_format || 'bin',
            file_id: f.digital_file_id,
            sample_file_id: f.is_sample_available ? (f.sample_file_id || null) : null,
            is_sample_available: !!(f.is_sample_available && f.sample_file_id),
            digital_price: typeof f.price === 'number' ? f.price : parseFloat(f.price || 0)
        }))

    const keptIds = new Set(desired.filter(d => d.digital_product_id).map(d => d.digital_product_id))
    const toDelete = (originalDigitalProducts.value || []).filter(dp => !keptIds.has(dp.id))

    for (const dp of toDelete) {
        try {
            await adminService.deleteDigitalProduct(dp.id)
        } catch (error) {
            console.error(`Failed to delete digital product ${dp.id}:`, error)
        }
    }

    for (const target of desired) {
        try {
            if (target.digital_product_id) {
                await adminService.updateDigitalProduct(target.digital_product_id, {
                    digital_price: target.digital_price,
                    sample_file_id: target.sample_file_id,
                    is_sample_available: target.is_sample_available
                })
            } else {
                await adminService.createDigitalProduct({
                    book_id: target.book_id,
                    format: target.format,
                    file_format: target.file_format,
                    file_id: target.file_id,
                    sample_file_id: target.sample_file_id,
                    is_sample_available: target.is_sample_available,
                    digital_price: target.digital_price
                })
            }
        } catch (error) {
            console.error('Failed to save digital product:', error)
            throw error
        }
    }
}
const showCropModal = ref(false)
const cropper = ref(null)
const cropImageSrc = ref('')
const cropResult = ref(null)
const selectedFile = ref(null)
const previewImageUrl = ref('')
const canZoomOut = ref(false)
const imageNaturalSize = ref({ width: 0, height: 0 })

// Category management state
const showCategoryForm = ref(false)
const editingCategory = ref(null)
const categoryForm = ref({
    name: '',
    description: ''
})

// Genre options are loaded from the categories API
const genreOptions = ref([])

const formData = ref({
    title: '',
    author: '',
    selectedAuthorId: null,
    description: '',
    cover_image: '',
    formats: [{ ...defaultFormat(), format_type: 'hardcover' }],
    published_date: '',
    page_count: null,
    genre: [],
    image_crop_dimensions: null,
    is_unavailable: false,
    is_test: false,
    also_available_from: ''
})

// Soft-deleted books are hidden by default; the toggle lists them (flagged)
// so they can be restored.
const showDeleted = ref(false)
const toggleShowDeleted = () => {
    showDeleted.value = !showDeleted.value
    loadBooks(1)
}

const loadBooks = async (page = 1) => {
    try {
        const data = await adminService.getBooks({
            page,
            limit: limit.value,
            include_all: true,
            include_deleted: showDeleted.value
        })
        if (data.pagination) {
            books.value = data.books || []
            pagination.value = data.pagination
            currentPage.value = page
        } else {
            books.value = Array.isArray(data) ? data : []
            pagination.value = null
        }
    } catch (error) {
        console.error('Failed to load books:', error)
        books.value = []
        pagination.value = null
    }
}

const loadAuthors = async () => {
    try {
        const data = await adminService.getAuthors({ limit: 1000 })
        if (data.pagination) {
            authors.value = data.authors || []
        } else {
            authors.value = Array.isArray(data) ? data : []
        }
    } catch (error) {
        console.error('Failed to load authors:', error)
        authors.value = []
    }
}

const loadCategories = async () => {
    try {
        const data = await adminService.getCategories({ limit: 1000 })
        if (data.pagination) {
            categories.value = data.categories || []
        } else {
            categories.value = Array.isArray(data) ? data : []
        }

        // Update genreOptions based on categories
        genreOptions.value = categories.value.map(cat => ({
            value: cat.slug,
            label: cat.name,
            id: cat.id,
            is_custom: cat.is_custom
        }))
    } catch (error) {
        console.error('Failed to load categories:', error)
        categories.value = []
        genreOptions.value = []
    }
}

const onAuthorSelect = () => {
    if (formData.value.selectedAuthorId) {
        const selectedAuthor = authors.value.find(a => a.id === formData.value.selectedAuthorId)
        if (selectedAuthor) {
            formData.value.author = selectedAuthor.name
        }
    }
}

const addNewBook = () => {
    showForm.value = true
    editingBook.value = null
}

const editBook = async (book) => {
    editingBook.value = book
    let genreArray = []
    if (Array.isArray(book.genre)) {
        genreArray = book.genre.filter(g => g && g.trim())
    } else if (typeof book.genre === 'string') {
        genreArray = book.genre.split(',').map(g => g.trim()).filter(g => g)
    }

    let selectedAuthorId = null
    try {
        const authorBooks = await adminService.getAuthorBooks(book.id)
        if (authorBooks && authorBooks.length > 0) {
            const author = authors.value.find(a => a.name === book.author)
            if (author) {
                selectedAuthorId = author.id
            }
        }
    } catch (error) {
        console.error('Failed to check author link:', error)
    }

    // Fetch full book data with formats and retailers
    let formatsArray = []
    try {
        const fullBookData = await adminService.getBook(book.id)
        if (fullBookData && fullBookData.formats && Array.isArray(fullBookData.formats)) {
            formatsArray = fullBookData.formats.map(f => ({
                ...defaultFormat(),
                id: f.id,
                format_type: ['hardcover', 'paperback', 'ebook', 'audiobook'].includes(f.format_type) ? f.format_type : 'custom',
                custom_format_type: ['hardcover', 'paperback', 'ebook', 'audiobook'].includes(f.format_type) ? '' : f.format_type,
                isbn: f.isbn || '',
                price: typeof f.price === 'number' ? f.price : parseFloat(f.price || 0),
                discount_percentage: f.discount_percentage || null,
                is_unavailable: f.is_unavailable || false,
                retailers: []
            }))

            // Map retailers to formats by book_format_id
            if (fullBookData.retailers && Array.isArray(fullBookData.retailers)) {
                formatsArray.forEach(format => {
                    const formatRetailers = fullBookData.retailers.filter(r => r.book_format_id === format.id)
                    format.retailers = formatRetailers.map(r => ({
                        name: r.name || '',
                        link: r.link || ''
                    }))
                })
            }
        } else if (book.formats && Array.isArray(book.formats) && book.formats.length > 0) {
            formatsArray = book.formats.map(f => ({
                ...defaultFormat(),
                format_type: ['hardcover', 'paperback', 'ebook', 'audiobook'].includes(f.format_type) ? f.format_type : 'custom',
                custom_format_type: ['hardcover', 'paperback', 'ebook', 'audiobook'].includes(f.format_type) ? '' : f.format_type,
                isbn: f.isbn || '',
                price: typeof f.price === 'number' ? f.price : parseFloat(f.price || 0),
                discount_percentage: f.discount_percentage || null,
                is_unavailable: f.is_unavailable || false,
                retailers: f.retailers && Array.isArray(f.retailers) ? f.retailers.map(r => ({
                    name: r.name || '',
                    link: r.link || ''
                })) : []
            }))
        } else {
            formatsArray = [{ ...defaultFormat(), format_type: 'hardcover' }]
        }
    } catch (error) {
        console.error('Failed to load full book data:', error)
        formatsArray = [{ ...defaultFormat(), format_type: 'hardcover' }]
    }

    // Fetch and merge existing digital products by format
    originalDigitalProducts.value = []
    try {
        const digitalProducts = await adminService.listDigitalProductsByBook(book.id)
        if (Array.isArray(digitalProducts)) {
            originalDigitalProducts.value = digitalProducts
            digitalProducts.forEach(dp => {
                const match = formatsArray.find(f => f.format_type === dp.format)
                if (match) {
                    match.digital_product_id = dp.id
                    match.digital_file_id = dp.file_id
                    match.digital_file_format = dp.file_format || ''
                    match.digital_file_name = dp.file_format ? `Existing ${dp.file_format} file` : 'Existing file'
                    match.sample_file_id = dp.sample_file_id || null
                    match.is_sample_available = !!dp.is_sample_available
                    if (dp.sample_file_id) {
                        match.sample_file_name = 'Existing sample file'
                    }
                }
            })
        }
    } catch (error) {
        console.error('Failed to load digital products:', error)
    }

    formData.value = {
        title: book.title,
        author: book.author,
        selectedAuthorId: selectedAuthorId,
        description: book.description,
        cover_image: book.cover_image || '',
        formats: formatsArray,
        published_date: book.published_date ? (typeof book.published_date === 'string' ? book.published_date.split('T')[0] : book.published_date) : '',
        page_count: book.page_count || null,
        genre: genreArray,
        image_crop_dimensions: book.image_crop_dimensions || null,
        is_unavailable: book.is_unavailable || false,
        is_test: book.is_test || false
    }
    imagePreview.value = ''
    showForm.value = true
}

const addFormat = () => {
    formData.value.formats.push(defaultFormat())
}

const addRetailer = (formatIndex) => {
    if (!formData.value.formats[formatIndex].retailers) {
        formData.value.formats[formatIndex].retailers = []
    }
    formData.value.formats[formatIndex].retailers.push({
        name: '',
        link: ''
    })
}

const removeRetailer = (formatIndex, retailerIndex) => {
    formData.value.formats[formatIndex].retailers.splice(retailerIndex, 1)
}

const removeFormat = (index) => {
    if (formData.value.formats.length > 1) {
        formData.value.formats.splice(index, 1)
    } else {
        alert('At least one format is required')
    }
}

const cancelForm = () => {
    showForm.value = false
    editingBook.value = null
    formData.value = {
        title: '',
        author: '',
        selectedAuthorId: null,
        description: '',
        cover_image: '',
        formats: [{ ...defaultFormat(), format_type: 'hardcover' }],
        published_date: '',
        page_count: null,
        genre: [],
        image_crop_dimensions: null,
        is_unavailable: false,
        is_test: false,
        also_available_from: ''
    }
    originalDigitalProducts.value = []
    digitalFileInputs.value = []
    sampleFileInputs.value = []
    imagePreview.value = ''
    uploading.value = false
    if (fileInput.value) {
        fileInput.value.value = ''
    }
    scrollToTop()
}

const handleFileSelect = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const isImage = file.type.startsWith('image/') ||
        file.name.toLowerCase().endsWith('.heic') ||
        file.name.toLowerCase().endsWith('.heif')
    if (!isImage) {
        alert('Please select an image file')
        return
    }

    if (file.size > 10 * 1024 * 1024) {
        alert('Image size must be less than 10MB')
        return
    }

    let blobUrl
    try {
        const { convertHeicForUpload, convertHeicForPreview } = await import('../../utils/heicConverter')
        const convertedFile = await convertHeicForUpload(file)
        selectedFile.value = convertedFile
        blobUrl = await convertHeicForPreview(file)
    } catch (error) {
        console.error('Failed to convert HEIC:', error)
        selectedFile.value = file
        blobUrl = URL.createObjectURL(file)
    }

    formData.value.cover_image = blobUrl
    imagePreview.value = blobUrl
}

const clearImage = () => {
    formData.value.cover_image = ''
    formData.value.image_crop_dimensions = null
    imagePreview.value = ''
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

const loadImageForCropping = async () => {
    const imageUrl = imagePreview.value || formData.value.cover_image
    if (!imageUrl) {
        alert('Please upload an image or enter an image URL first')
        return
    }

    let blobUrl = imagePreview.value

    if (!blobUrl || !blobUrl.startsWith('blob:')) {
        try {
            let blob
            if (imageUrl.includes('storage.googleapis.com')) {
                const proxyUrl = `${API_BASE_URL}/api/admin/proxy/image?url=${encodeURIComponent(imageUrl)}`
                const response = await fetch(proxyUrl, {
                    credentials: 'include'
                })
                if (!response.ok) {
                    throw new Error('Failed to load image')
                }
                blob = await response.blob()
            } else {
                const response = await fetch(imageUrl)
                if (!response.ok) {
                    throw new Error('Failed to load image')
                }
                blob = await response.blob()
            }

            blobUrl = URL.createObjectURL(blob)
            imagePreview.value = blobUrl
        } catch (error) {
            console.error('Failed to load image for cropping:', error)
            alert('Failed to load image. Please check the image URL or try uploading the image again.')
            return
        }
    }

    cropImageSrc.value = blobUrl
    cropResult.value = null
    selectedFile.value = null
    showCropModal.value = true
    await nextTick()
}

const closeCropModal = () => {
    const fileToUpload = selectedFile.value

    if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
        URL.revokeObjectURL(cropImageSrc.value)
    }
    showCropModal.value = false
    cropResult.value = null
    cropImageSrc.value = ''
    previewImageUrl.value = ''
    canZoomOut.value = false
    imageNaturalSize.value = { width: 0, height: 0 }

    if (fileToUpload && !formData.value.cover_image) {
        imagePreview.value = ''
        if (fileInput.value) {
            fileInput.value.value = ''
        }
    } else if (!formData.value.cover_image) {
        imagePreview.value = ''
    }

    selectedFile.value = null
}

const onCropperReady = () => {
    if (cropper.value) {
        const result = cropper.value.getResult()
        if (result?.imageSize) {
            imageNaturalSize.value = {
                width: result.imageSize.width,
                height: result.imageSize.height
            }
        }
    }
}

const onCropChange = ({ coordinates, canvas, visibleArea }) => {
    cropResult.value = { coordinates, canvas, visibleArea }
    if (canvas) {
        previewImageUrl.value = canvas.toDataURL('image/jpeg', 0.95)
    }

    if (cropper.value && coordinates) {
        const cropWidth = coordinates.width
        const cropHeight = coordinates.height

        if (cropWidth && cropHeight) {
            const imageWidth = imageNaturalSize.value.width || visibleArea?.width || 0
            const imageHeight = imageNaturalSize.value.height || visibleArea?.height || 0

            if (imageWidth && imageHeight) {
                const needsZoomOut = imageWidth > cropWidth || imageHeight > cropHeight
                canZoomOut.value = needsZoomOut
            }
        }
    }

    console.log('Crop changed:', coordinates)
}

const applyCrop = async () => {
    if (!cropper.value || !cropResult.value) {
        alert('Please select a crop area by adjusting the crop box')
        return
    }

    try {
        const { coordinates, canvas } = cropResult.value

        if (!coordinates || !coordinates.width || !coordinates.height) {
            alert('Please select a crop area')
            return
        }

        formData.value.image_crop_dimensions = {
            width: coordinates.width,
            height: coordinates.height
        }

        if (canvas) {
            uploading.value = true

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    alert('Failed to create cropped image')
                    uploading.value = false
                    return
                }

                try {
                    const currentImageUrl = formData.value.cover_image || imagePreview.value
                    let fileName = selectedFile.value?.name || (currentImageUrl ? currentImageUrl.split('/').pop().split('?')[0] : 'cropped-image.jpg')
                    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '')
                    fileName = `${nameWithoutExt}.jpg`

                    const formDataUpload = new FormData()
                    formDataUpload.append('file', blob, fileName)

                    const uploadedUrl = await adminService.uploadImage(formDataUpload)
                    if (uploadedUrl) {
                        formData.value.cover_image = uploadedUrl
                        imagePreview.value = uploadedUrl

                        if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
                            URL.revokeObjectURL(cropImageSrc.value)
                        }

                        uploading.value = false
                        showCropModal.value = false
                        selectedFile.value = null
                        cropResult.value = null
                    } else {
                        throw new Error('No URL returned from server')
                    }
                } catch (error) {
                    console.error('Failed to upload cropped image:', error)
                    const errorMessage = error.response?.data?.error || error.message || 'Failed to upload cropped image. Please try again.'
                    alert(errorMessage)
                    uploading.value = false
                }
            }, 'image/jpeg', 0.95)
        } else {
            showCropModal.value = false
        }
    } catch (error) {
        console.error('Error applying crop:', error)
        alert('Failed to apply crop. Please try again.')
    }
}

const fitImageToCrop = async () => {
    if (!cropper.value || !cropResult.value?.coordinates) {
        return
    }

    try {
        const coordinates = cropResult.value.coordinates
        const cropWidth = coordinates.width
        const cropHeight = coordinates.height

        if (!cropWidth || !cropHeight) {
            return
        }

        const imageWidth = imageNaturalSize.value.width
        const imageHeight = imageNaturalSize.value.height

        if (!imageWidth || !imageHeight) {
            const result = cropper.value.getResult()
            if (result?.imageSize) {
                imageNaturalSize.value = {
                    width: result.imageSize.width,
                    height: result.imageSize.height
                }
            } else {
                return
            }
        }

        const finalImageWidth = imageNaturalSize.value.width
        const finalImageHeight = imageNaturalSize.value.height

        if (!finalImageWidth || !finalImageHeight) {
            return
        }

        const scaleX = cropWidth / finalImageWidth
        const scaleY = cropHeight / finalImageHeight
        const scale = Math.min(scaleX, scaleY)

        if (scale < 1 && cropper.value.zoom) {
            const result = cropper.value.getResult()
            const currentZoom = result?.transforms?.zoom || 1
            const newZoom = Math.max(0.1, Math.min(10, currentZoom * scale))

            cropper.value.zoom(newZoom)

            await nextTick()

            if (cropper.value.move) {
                const updatedResult = cropper.value.getResult()
                const visibleArea = updatedResult?.visibleArea || {}
                const centerX = visibleArea.left + (visibleArea.width / 2)
                const centerY = visibleArea.top + (visibleArea.height / 2)

                cropper.value.move({
                    left: centerX - (cropWidth / 2),
                    top: centerY - (cropHeight / 2)
                })
            }
        }
    } catch (error) {
        console.error('Error fitting image to crop:', error)
    }
}

const getPreviewBookStyle = (coordinates) => {
    if (!coordinates || !coordinates.width || !coordinates.height) {
        return { width: '168px', height: '224px' }
    }

    let displayWidth = coordinates.width
    let displayHeight = coordinates.height

    const MAX_WIDTH = 200
    if (coordinates.width > MAX_WIDTH) {
        const scale = MAX_WIDTH / coordinates.width
        displayWidth = MAX_WIDTH
        displayHeight = coordinates.height * scale
    }

    return {
        width: `${displayWidth}px`,
        height: `${displayHeight}px`
    }
}

const getReferenceBookStyle = () => {
    return {
        width: '168px',
        height: '224px'
    }
}

const Math = window.Math

const getImageStyle = () => {
    if (formData.value.image_crop_dimensions &&
        formData.value.image_crop_dimensions.width > 0 &&
        formData.value.image_crop_dimensions.height > 0) {
        const { width, height } = formData.value.image_crop_dimensions
        const maxWidth = 200
        let displayWidth = width
        let displayHeight = height

        if (width > maxWidth) {
            const scale = maxWidth / width
            displayWidth = maxWidth
            displayHeight = height * scale
        }

        return {
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            objectFit: 'cover'
        }
    }
    return {
        maxWidth: '200px',
        height: '192px',
        objectFit: 'cover'
    }
}


const saveBook = async () => {
    try {
        // First, upload the blob image if there's a selected file that hasn't been uploaded yet
        if (selectedFile.value && imagePreview.value && imagePreview.value.startsWith('blob:')) {
            uploading.value = true
            try {
                const formDataUpload = new FormData()
                formDataUpload.append('file', selectedFile.value, selectedFile.value.name)
                const uploadedUrl = await adminService.uploadImage(formDataUpload)
                formData.value.cover_image = uploadedUrl
                imagePreview.value = uploadedUrl
                selectedFile.value = null
            } catch (error) {
                console.error('Failed to upload image:', error)
                alert('Failed to upload image. Please try again.')
                uploading.value = false
                return
            } finally {
                uploading.value = false
            }
        }

        if (!formData.value.genre || !Array.isArray(formData.value.genre) || formData.value.genre.length === 0) {
            alert('Please select at least one category')
            return
        }

        if (!formData.value.formats || !Array.isArray(formData.value.formats) || formData.value.formats.length === 0) {
            alert('Please add at least one format')
            return
        }

        const selectedAuthorId = formData.value.selectedAuthorId

        const formats = formData.value.formats.map(f => {
            let formatType = f.format_type
            if (formatType === 'custom') {
                if (!f.custom_format_type || !f.custom_format_type.trim()) {
                    throw new Error('Custom format type is required when selecting custom format')
                }
                formatType = f.custom_format_type.trim()
            }
            if (!formatType || !f.isbn || !f.isbn.trim() || f.price === null || f.price === undefined) {
                throw new Error('All formats must have format type, ISBN, and price')
            }
            const retailers = (f.retailers || []).filter(r => r.name && r.name.trim() && r.link && r.link.trim()).map(r => ({
                name: r.name.trim(),
                link: r.link.trim()
            }))
            return {
                format_type: formatType,
                isbn: f.isbn.trim(),
                price: parseFloat(f.price),
                discount_percentage: f.discount_percentage || null,
                is_unavailable: f.is_unavailable || false,
                retailers: retailers.length > 0 ? retailers : null
            }
        })

        const data = {
            ...formData.value,
            formats: formats,
            genre: formData.value.genre.filter(g => g)
        }
        delete data.selectedAuthorId

        if (data.cover_image && data.image_crop_dimensions &&
            data.image_crop_dimensions.width > 0 && data.image_crop_dimensions.height > 0) {
            try {
                const { resizeImage } = await import('../../utils/imageResize.js')
                const resizedBlob = await resizeImage(
                    data.cover_image,
                    data.image_crop_dimensions.width,
                    data.image_crop_dimensions.height
                )
                const formDataUpload = new FormData()
                formDataUpload.append('file', resizedBlob, 'resized-image.jpg')
                const resizedUrl = await adminService.uploadImage(formDataUpload)
                data.cover_image = resizedUrl
            } catch (error) {
                console.error('Failed to resize image:', error)
            }
        }

        let bookId
        if (editingBook.value) {
            await adminService.updateBook(editingBook.value.id, data)
            bookId = editingBook.value.id
        } else {
            const result = await adminService.createBook(data)
            bookId = result.book ? result.book.id : result.id
        }

        if (selectedAuthorId && bookId) {
            try {
                await adminService.linkBookToAuthor(bookId, selectedAuthorId)
            } catch (error) {
                console.error('Failed to link book to author:', error)
            }
        }

        if (bookId) {
            try {
                await syncDigitalProducts(bookId)
            } catch (error) {
                console.error('Failed to sync digital products:', error)
                alert('Book saved, but failed to sync digital product files. Please check the digital uploads.')
            }
        }

        await loadBooks(currentPage.value)
        cancelForm()
        scrollToTop()
    } catch (error) {
        console.error('Failed to save book:', error)
        alert('Failed to save book. Please try again.')
    }
}

const deleteBook = async (id) => {
    if (!confirm('Remove this book from the store?\n\nIt will no longer be visible or purchasable, but customers who already bought it keep their ebooks/audiobooks and purchase history. You can restore it later via "Show deleted books".')) return

    try {
        await adminService.deleteBook(id)
        await loadBooks(currentPage.value)
    } catch (error) {
        console.error('Failed to delete book:', error)
        alert('Failed to delete book. Please try again.')
    }
}

const restoreBook = async (id) => {
    try {
        await adminService.restoreBook(id)
        await loadBooks(currentPage.value)
    } catch (error) {
        console.error('Failed to restore book:', error)
        alert('Failed to restore book. Please try again.')
    }
}

// Category Management Methods
const sortedCategories = computed(() => {
    return [...categories.value].sort((a, b) => a.name.localeCompare(b.name))
})

const editCategory = (category) => {
    editingCategory.value = category
    categoryForm.value = {
        name: category.name,
        description: category.description || ''
    }
    showCategoryForm.value = true
}

const cancelCategoryForm = () => {
    showCategoryForm.value = false
    editingCategory.value = null
    categoryForm.value = {
        name: '',
        description: ''
    }
    scrollToTop()
}

const saveCategory = async () => {
    try {
        if (editingCategory.value) {
            // Update existing category
            await adminService.updateCategory(editingCategory.value.id, categoryForm.value)
        } else {
            // Create new category
            await adminService.createCategory(categoryForm.value)
        }

        // Reload categories
        await loadCategories()
        cancelCategoryForm()
    } catch (error) {
        console.error('Failed to save category:', error)
        alert('Failed to save category. Please try again.')
    }
}

const deleteCategoryById = async (categoryId) => {
    const category = categories.value.find(c => c.id === categoryId)
    if (!category) return

    if (!confirm(`Are you sure you want to delete the category "${category.name}"? This will remove it from all books that use it.`)) {
        return
    }

    try {
        await adminService.deleteCategory(categoryId)

        // Reload categories
        await loadCategories()

        // Reload books to reflect changes
        await loadBooks(currentPage.value)
    } catch (error) {
        console.error('Failed to delete category:', error)
        alert('Failed to delete category. Please try again.')
    }
}

onMounted(() => {
    loadBooks(1)
    loadAuthors()
    loadCategories()
})
</script>

<style scoped>
.admin-content-container {
    width: 100%;
    box-sizing: border-box;
}
</style>

<style>
.vue-advanced-cropper {
    width: 100%;
    height: 100%;
}
</style>
